import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { DefaultButton, Icon, PrimaryButton } from "@fluentui/react";
import { AppRoutes } from "../../AppRoutes";
import Tr from "../../Utils/Translations/Translations";
import SettingsUtil from "../../Utils/Settings";
import ImageAssetLoader from "../../Components/ImageAssetLoader/ImageAssetLoader";
import Footer from "../../Components/Footer/Footer";
import "./HomePage.scss";
import {
  ISubscription,
  ISubscriptionFeature,
} from "../../Models/ISubscription";
import { GlobalState } from "../../Redux/RootReducer";

const FeatureCard = (params: {
  title: string;
  description: string;
  iconSrc: string;
  bgSrc: string;
}) => (
  <div className="home-page-feature-card">
    <div className="feature-card-bg">
      <ImageAssetLoader src={params.bgSrc} />
    </div>
    <div className="feature-card-content">
      <div className="feature-card-icon">
        <ImageAssetLoader src={params.iconSrc} />
      </div>
      <h3 className="feature-card-title">{params.title}</h3>
      <p className="feature-card-desc">{params.description}</p>
    </div>
  </div>
);

// --- Sotto-Componente: Pricing Card Custom (Dinamica da Redux) ---
const HomePricingCard = (params: {
  subscription: ISubscription;
  features: ISubscriptionFeature[]; // Features possedute da QUESTA sub
  allFeatures: { code: string; label: string }[]; // Lista GLOBALE features
  isPopular: boolean;
  onAction: () => void;
}) => {
  // Funzione helper per formattare la quantità
  const getFeatureLabel = (feature: ISubscriptionFeature) => {
    const isAiFeature = ["QUERY_BOT", "EVENT_ERROR_DETECTION"].includes(
      feature.FeatureCode
    );
    if (isAiFeature) {
      return `${feature.FeatureAmount} ${Tr.Translate(
        "subscription",
        "per_month_calls"
      )}`;
    }
    return feature.FeatureAmount;
  };

  return (
    <div className={`pricing-card ${params.isPopular ? "popular" : ""}`}>
      {params.isPopular && <div className="popular-badge">Consigliato</div>}

      <div className="pricing-header">
        <div className="pricing-icon">
          <ImageAssetLoader src={params.subscription.SubscriptionImage} />
        </div>
        <h4>
          {Tr.Translate("subscription", params.subscription.SubscriptionName)}
        </h4>
        <div className="pricing-cost">
          <span className="amount">{params.subscription.ExposedPrice} €</span>
          <span className="period">
            {" "}
            / {Tr.Translate("subscription", "month")}
          </span>
        </div>
        <p className="pricing-desc">
          {Tr.Translate(
            "subscription",
            params.subscription.SubscriptionDescription
          )}
        </p>
      </div>

      <div className="pricing-features-wrap">
        <div className="features-title">
          {Tr.Translate("subscription", "subscription_includes")}:
        </div>
        <ul className="pricing-features-list">
          {/* Iteriamo sulla lista GLOBALE delle features per fare il confronto */}
          {params.allFeatures.map((masterFeat) => {
            // Cerchiamo se questo abbonamento ha questa feature
            const activeFeat = params.features.find(
              (f) => f.FeatureCode === masterFeat.code && f.FeatureAmount > 0
            );

            return (
              <li
                key={masterFeat.code}
                className={!activeFeat ? "missing" : ""}
              >
                <div className="li-left">
                  {activeFeat ? (
                    <Icon iconName="CheckMark" className="icon-check" />
                  ) : (
                    <Icon iconName="Cancel" className="icon-cross" />
                  )}
                  <span className="feat-name">
                    {Tr.Translate("subscription", masterFeat.label)}
                  </span>
                </div>

                {/* Mostra quantità solo se la feature è presente */}
                {activeFeat && (
                  <span className="feat-amount">
                    {getFeatureLabel(activeFeat)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// --- Sotto-Componente: Carousel Container ---
const HomePricingCarousel = () => {
  const navigate = useNavigate();

  const subscriptions: ISubscription[] = useSelector(
    (state: GlobalState) => state.generic.subscriptions
  );
  const subscriptionFeatures: ISubscriptionFeature[] = useSelector(
    (state: GlobalState) => state.generic.subscriptionFeatures
  );

  // 1. Filtriamo le sottoscrizioni valide
  const sortedSubs = useMemo(() => {
    return [...subscriptions]
      .sort((a, b) => a.ExposedPrice - b.ExposedPrice)
      .filter(
        (x) => !x.DevelopItem || window.location.origin.includes("localhost")
      );
  }, [subscriptions]);

  // 2. Calcoliamo la "Master List" di tutte le feature uniche disponibili nei piani visibili
  const allUniqueFeatures = useMemo(() => {
    const uniqueMap = new Map<string, string>();

    // Iteriamo su tutte le features
    subscriptionFeatures.forEach((feat) => {
      // Consideriamo solo features che appartengono a sottoscrizioni visibili (sortedSubs)
      const belongsToVisibleSub = sortedSubs.some(
        (s) => s.Id === feat.SubscriptionId
      );

      if (belongsToVisibleSub && !uniqueMap.has(feat.FeatureCode)) {
        uniqueMap.set(feat.FeatureCode, feat.FeatureLabel);
      }
    });

    // Convertiamo in array
    return Array.from(uniqueMap.entries()).map(([code, label]) => ({
      code,
      label,
    }));
  }, [subscriptionFeatures, sortedSubs]);

  if (sortedSubs.length === 0) return null;

  return (
    <div className="pricing-carousel-container">
      <div className="pricing-track">
        {sortedSubs.map((sub, index) => {
          // Filtriamo le features possedute specificamente da questa sub
          const myFeatures = subscriptionFeatures.filter(
            (f) => f.SubscriptionId === sub.Id
          );

          // Esempio logica popolarità: il secondo piano (spesso "Single") è popolare
          const isPopular = index === 1;

          return (
            <HomePricingCard
              key={sub.Id}
              subscription={sub}
              features={myFeatures}
              allFeatures={allUniqueFeatures} // Passiamo la lista completa per il confronto
              isPopular={isPopular}
              onAction={() => navigate(AppRoutes.STORE_PAGE)}
            />
          );
        })}
      </div>
    </div>
  );
};

// --- Componente Principale HomePage ---
const HomePage = () => {
  const navigate = useNavigate();

  // Logica Login Automatico (Preservata)
  useEffect(() => {
    const shouldRemember = SettingsUtil.GetSettings("remember_me");
    const autoLoginState = localStorage.getItem("do-login-automatic");

    if (shouldRemember && autoLoginState !== "2") {
      localStorage.setItem("do-login-automatic", "1");
      navigate(AppRoutes.LOGIN_PAGE);
    }
  }, [navigate]);

  // Configurazione delle Features Statiche
  const features = [
    {
      title: Tr.Translate("presentation", "presentation_4"),
      desc: Tr.Translate("presentation", "presentation_5"),
      icon: "/Assets/Public/timeline_ico_2.png",
      bg: "/Assets/Public/space_travel.gif", // TODO: Sostituisci con img specifica
    },
    {
      title: Tr.Translate("presentation", "presentation_6"),
      desc: Tr.Translate("presentation", "presentation_7"),
      icon: "/Assets/Public/ai_icon.png",
      bg: "/Assets/Public/space_travel.gif", // TODO: Sostituisci con img specifica
    },
    {
      title: Tr.Translate("presentation", "presentation_8"),
      desc: Tr.Translate("presentation", "presentation_9"),
      icon: "/Assets/Public/teamwork_2.png",
      bg: "/Assets/Public/space_travel.gif", // TODO: Sostituisci con img specifica
    },
  ];

  return (
    <div className="home-page-wrap">
      {/* HERO SECTION */}
      <section className="home-hero">
        <div className="home-page-banner-line">
          <h1 className="home-page-text-big">
            {Tr.Translate("presentation", "presentation_1")}
          </h1>
          <h2 className="home-page-text-normal">
            {Tr.Translate("presentation", "presentation_2")}
          </h2>
        </div>
        <div className="home-page-huge-title">
          {Tr.Translate("presentation", "presentation_3")}
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section className="home-features">
        <div className="home-page-tile-row">
          {features.map((feat, index) => (
            <FeatureCard
              key={index}
              title={feat.title}
              description={feat.desc}
              iconSrc={feat.icon}
              bgSrc={feat.bg}
            />
          ))}
        </div>
      </section>

      {/* UX / USABILITY SECTION */}
      <section className="home-usability">
        <div className="home-page-huge-title">
          {Tr.Translate("presentation", "presentation_10")}
        </div>
        <div className="home-page-min-title">
          {Tr.Translate("presentation", "presentation_11")}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="home-cta-block">
        <div className="cta-item">
          <div className="home-page-text-big">
            {Tr.Translate("presentation", "presentation_12")}
          </div>
          <p className="home-page-text-normal">
            {Tr.Translate("presentation", "presentation_12_2").trim() + ":"}
          </p>
          <DefaultButton
            iconProps={{ iconName: "Message" }}
            text={Tr.Translate("presentation", "presentation_13")}
            onClick={() => navigate(AppRoutes.CONTACT_US_PAGE)}
            styles={{ root: { padding: "20px" } }}
          />
        </div>

        <div className="cta-divider"></div>

        <div className="cta-item">
          <div className="home-page-text-big">
            {/* Esempio di testo diretto o usa una traduzione se esiste */}
            {Tr.Translate("presentation", "presentation_14")}
          </div>
          <p className="home-page-text-normal">
            {Tr.Translate("presentation", "presentation_14_2")}
          </p>
          <PrimaryButton
            text={Tr.Translate("language", "register")} // O meglio "Registrati" se hai la key
            onClick={() => navigate(AppRoutes.REGISTRATION_PAGE)}
            styles={{ root: { padding: "20px", fontSize: "1.1em" } }}
          />
        </div>
      </section>

      {/* PRICING / STORE SECTION (Redux Powered) */}
      <section className="home-pricing">
        <div className="home-page-huge-title">
          {Tr.Translate("presentation", "presentation_15")}
        </div>
        <div className="home-page-min-title">
          {Tr.Translate("presentation", "presentation_16")}
        </div>

        <HomePricingCarousel />
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
